import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';

export default function IrreplacableExampleReportSection() {
  const navigate = useNavigate();
  
  const handleViewReport = () => {
    navigate('/example-report');
  };

  return (
    <section id="example-report-section" className="py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Top border with gradient effect */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/4 w-1/2 h-1/2 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/4 w-1/2 h-1/2 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-1/3 h-1/3 bg-[#f9c55f]/5 rounded-full blur-3xl" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col lg:flex-row items-center justify-between">
          {/* Mobile Image (above content) */}
          <div className="lg:hidden w-full mb-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="cursor-pointer"
              onClick={handleViewReport}
            >
              <img 
                src="/ReportHero.webp" 
                alt="Example Report Preview" 
                className="w-full h-auto rounded-xl max-w-lg mx-auto"
              />
            </motion.div>
          </div>
            
          {/* Mobile Content (below image) */}
          <div className="lg:hidden text-center mb-8">
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-block relative"
              >
                <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 text-primary px-6 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm border border-primary/10">
                  Preview Our Reports
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 blur-xl opacity-50 -z-10" />
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent mb-6"
              >
                Experience the Power of Our Feedback Reports
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              >
                Get a firsthand look at how our comprehensive feedback analysis can transform your professional growth journey
              </motion.p>
            </>
          </div>

          {/* Desktop Content (left side) */}
          <div className="lg:w-1/2 lg:pr-8">
            <div className="hidden lg:block text-left mb-8">
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="inline-block relative"
                >
                  <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 text-primary px-6 py-2 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm border border-primary/10">
                    Preview Our Reports
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 blur-xl opacity-50 -z-10" />
                </motion.div>
                
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent mb-6"
                >
                  Experience the Power of Our Feedback Reports
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="text-xl text-gray-600 max-w-xl leading-relaxed"
                >
                  Get a firsthand look at how our comprehensive feedback analysis can transform your professional growth journey
                </motion.p>
              </>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex justify-center lg:justify-start mt-4"
            >
              <button
                onClick={handleViewReport}
                className="group relative bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto] animate-gradient text-dark px-10 py-5 rounded-xl hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-3 overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/10 rounded-xl transform group-hover:scale-105 transition-transform duration-300" />
                <FileText className="w-6 h-6 relative z-10" />
                <span className="font-medium text-lg relative z-10">View Free Example Report</span>
                <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 blur group-hover:blur-md transition-all duration-300" />
              </button>
            </motion.div>
          </div>
          
          {/* Desktop Image (right side) */}
          <div className="hidden lg:block lg:w-1/2 lg:pl-8">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="cursor-pointer"
              onClick={handleViewReport}
            >
              <img 
                src="/ReportHero.webp" 
                alt="Example Report Preview" 
                className="w-full h-auto rounded-xl"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom border with gradient effect */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </section>
  );
}